/**
 * Sơ đồ kho Summary Nhơn Trạch — bám Access `frmFactoryNtSummarySubForm2`
 * + screenshot `plan/map_nt.png`.
 *
 *   KHO A (Top=7068):  4 hàng × 8 cột. Nhãn hàng 4→1 (trái), nhãn cột A→H (đáy).
 *                      32 ô = NT.A.{col}{row}, vd "NT.A.A4".
 *
 *   KHO C (Top=11484): 8 hàng × 4 cột. Nhãn hàng 8→1 (trái), nhãn cột A→D (đáy).
 *                      Cột A chỉ có A1,A2,A7,A8; rows 3–6 cột A = khối
 *                      "KHU VỰC SẢN XUẤT" (WIP, vàng) — value = kvSx.
 *
 * Allocation ("PHÂN BỔ TỒN KHO") lấy số API (`summaryWarehouseSplit`), KHÔNG
 * tự cộng lại client-side. Fallback gom từ mapCells khi API thiếu split.
 */
import { Fragment, useMemo } from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import type { FactoryCell, FactorySummaryWarehouseSplit } from '@/store/api/factoryApi'
import { PANE_8_2 } from '@/theme/responsive'
import { BlockTitle } from './BlockTitle'
import type { FactorySummaryMapCell } from '../baoLoc/FactoryBaoLocSummaryMap'

interface Props {
  mapCells: FactorySummaryMapCell[]
  sumRow?: Record<string, FactoryCell> | null
  kvSx?: number | null
  warehouseSplit?: FactorySummaryWarehouseSplit | null
  scope?: 'all' | 'kho-a' | 'kho-bc' | 'allocation'
  title?: string
  subtitle?: string
  factoryName?: string
}

function asNumber(v: FactoryCell): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function fmtQty(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—'
  return v.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

/** Màu nhãn trục (hàng + cột) — đỏ như Access. */
const MAP_AXIS_COLOR = '#d32f2f'

// Access spec: KHO A = 4 rows × 8 cols
const KHO_A_COLS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const
const KHO_A_ROWS = [4, 3, 2, 1] as const

// Access spec: KHO C = 8 rows × 4 cols
const KHO_C_COLS = ['A', 'B', 'C', 'D'] as const
const KHO_C_ROWS = [8, 7, 6, 5, 4, 3, 2, 1] as const
// Cột A rows 3–6 = khối WIP "KHU VỰC SẢN XUẤT" (không có ô CA3..CA6 riêng).
const KHO_C_WIP_CELLS = new Set(['A3', 'A4', 'A5', 'A6'])

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  px: 0.25,
  py: 0.25,
  minHeight: 40,
  textAlign: 'center' as const,
}

const rowLabelSx = {
  ...cellSx,
  borderLeft: 0,
  color: MAP_AXIS_COLOR,
  fontSize: '0.7rem',
  fontWeight: 800,
}

const colLabelSx = {
  ...cellSx,
  border: 0,
  minHeight: 16,
  color: MAP_AXIS_COLOR,
  fontSize: '0.7rem',
  fontWeight: 800,
}

// Style tiêu đề khu — đồng bộ với Map Bảo Lộc (sectionTitleSx).
// Style dòng allocation — lấy Bảo Lộc làm chuẩn (allocationRowSx + AllocationLine).
const allocationRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 1,
  py: 0.4,
  px: 0.75,
  borderRadius: 0.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(0,0,0,0.02)',
}

const sectionTitleSx = {
  display: 'block',
  mb: 0.5,
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: 0.04,
  color: 'text.secondary',
}

type Zone = 'A' | 'C'

/** Ô khu vực — style đồng bộ Map Bảo Lộc (AreaCell): nền trắng, mã trên, số dưới. */
function DataCell({ code, qty }: { code: string; qty: number | null }) {
  const present = qty !== null && Number.isFinite(qty)
  return (
    <Box sx={cellSx} title={code}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ lineHeight: 1.05, fontSize: '0.56rem', color: present ? 'text.primary' : 'text.disabled' }}
      >
        {code.split('.').slice(2).join('.')}
      </Typography>
      {present ? (
        <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.68rem' }}>
          {fmtQty(qty)}
        </Typography>
      ) : null}
    </Box>
  )
}

export function NhonTrachSummaryMap({
  mapCells,
  kvSx,
  warehouseSplit,
  title,
  scope = 'all',
}: Props) {
  // Lookup tồn theo full_area_code `NT.<zone>.<col><row>` (vd "NT.A.A4").
  const lookup = useMemo(() => {
    const byFull = new Map<string, number>()
    for (const c of mapCells) {
      const v = asNumber(c.value) ?? 0
      if (c.field) {
        const k = String(c.field).trim().toUpperCase()
        if (k) byFull.set(k, (byFull.get(k) ?? 0) + v)
      }
    }
    return (zone: Zone, col: string, row: number): number | null => {
      const k = `NT.${zone}.${col.toUpperCase()}${row}`
      return byFull.get(k) ?? null
    }
  }, [mapCells])

  const kv = asNumber(kvSx ?? null) ?? 0

  // Allocation rows — số do API trả về; KHÔNG tự cộng lại client-side.
  const allocationRows = useMemo(() => {
    if (warehouseSplit) {
      return [
        { labelVi: 'Total (stack + WIP)', value: warehouseSplit.totalStackAndKv, bold: true },
        { labelVi: 'Warehouse A (NT.A.*)', value: warehouseSplit.zoneAWithKv },
        { labelVi: 'Warehouse C (NT.C.*)', value: warehouseSplit.zoneC },
        { labelVi: 'Unassigned area', value: warehouseSplit.noArea },
        { labelVi: 'Processing Area', value: warehouseSplit.kvSx },
      ]
    }
    let za = 0, zc = 0
    for (const c of mapCells) {
      const f = (c.field || '').toUpperCase()
      const n = asNumber(c.value) ?? 0
      if (f.startsWith('NT.A.')) za += n
      else if (f.startsWith('NT.C.')) zc += n
    }
    return [
      { labelVi: 'Total (stack + WIP)', value: za + zc + kv, bold: true },
      { labelVi: 'Warehouse A (NT.A.*)', value: za + kv },
      { labelVi: 'Warehouse C (NT.C.*)', value: zc },
      { labelVi: 'Processing Area', value: kv },
    ]
  }, [mapCells, kv, warehouseSplit])

  const showKhoA = scope === 'all' || scope === 'kho-a'
  // 'kho-bc' giữ tên scope cũ để tương thích parent — NT chỉ có kho C.
  const showKhoC = scope === 'all' || scope === 'kho-bc'
  const showAllocation = scope === 'all' || scope === 'allocation'

  return (
    <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <BlockTitle title={title ?? 'Factory map'} />

      {/* 2 cột: bên trái map (KHO A + KHO C), bên phải allocation. Tỷ lệ 7:3. */}
      <Box sx={{ ...PANE_8_2, gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' }, gap: 1.5, alignItems: 'start' }}>
      <Box sx={{ minWidth: 0 }}>

      {/* KHO A — 4 hàng × 8 cột; nhãn hàng trái, nhãn cột đáy */}
      {showKhoA && (
        <Box sx={{ mb: 1.5 }}>
          <Typography component="span" sx={sectionTitleSx}>Warehouse A</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `24px repeat(${KHO_A_COLS.length}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${KHO_A_ROWS.length}, minmax(40px, auto)) 16px`,
              gap: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              overflow: 'hidden',
              minWidth: { xs: 560, md: 'auto' },
            }}
          >
            {KHO_A_ROWS.map((row, ri) => (
              <Fragment key={`row-A-${row}`}>
                <Box sx={{ ...rowLabelSx, gridColumn: 1, gridRow: ri + 1 }}>{row}</Box>
                {KHO_A_COLS.map((col) => (
                  <DataCell key={`A-${col}${row}`} code={`NT.A.${col}${row}`} qty={lookup('A', col, row)} />
                ))}
              </Fragment>
            ))}
            {/* Nhãn cột A–H ở đáy */}
            <Box sx={{ ...colLabelSx, gridColumn: 1, gridRow: KHO_A_ROWS.length + 1 }} />
            {KHO_A_COLS.map((col) => (
              <Box key={`cl-A-${col}`} sx={{ ...colLabelSx, gridRow: KHO_A_ROWS.length + 1 }}>{col}</Box>
            ))}
          </Box>
        </Box>
      )}

      {/* KHO C — 8 hàng × 4 cột; cột A rows 3–6 = khối WIP vàng */}
      {showKhoC && (
        <Box>
          <Typography component="span" sx={sectionTitleSx}>Warehouse C</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `24px repeat(${KHO_C_COLS.length}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${KHO_C_ROWS.length}, minmax(40px, auto)) 16px`,
              gap: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              overflow: 'hidden',
              maxWidth: 460,
            }}
          >
            {KHO_C_ROWS.map((row, ri) => (
              <Fragment key={`row-C-${row}`}>
                <Box sx={{ ...rowLabelSx, gridColumn: 1, gridRow: ri + 1 }}>{row}</Box>
                {KHO_C_COLS.map((col) => {
                  if (KHO_C_WIP_CELLS.has(`${col}${row}`)) return null
                  return (
                    <DataCell key={`C-${col}${row}`} code={`NT.C.${col}${row}`} qty={lookup('C', col, row)} />
                  )
                })}
              </Fragment>
            ))}
            {/* Khối WIP "KHU VỰC SẢN XUẤT" — cột A (gridCol 2) × rows 6→3 */}
            <Box
              sx={{
                ...cellSx,
                gridColumn: 2,
                gridRow: '3 / 7',
                bgcolor: 'rgba(255, 235, 120, 0.55)',
                p: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, lineHeight: 1.15, textAlign: 'center' }}>
                Processing Area
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, lineHeight: 1.1, mt: 0.5 }}>
                {fmtQty(kv)}
              </Typography>
            </Box>
            {/* Nhãn cột A–D ở đáy */}
            <Box sx={{ ...colLabelSx, gridColumn: 1, gridRow: KHO_C_ROWS.length + 1 }} />
            {KHO_C_COLS.map((col) => (
              <Box key={`cl-C-${col}`} sx={{ ...colLabelSx, gridRow: KHO_C_ROWS.length + 1 }}>{col}</Box>
            ))}
          </Box>
        </Box>
      )}

      </Box>

      {/* Cột phải: STOCK ALLOCATION (tỷ lệ 3/10) */}
      {showAllocation && (
        <Box sx={{ minWidth: 0 }}>
          <BlockTitle title="Stock allocation" />
          <Stack spacing={0.5}>
            {allocationRows.map((r) => (
              <AllocLine key={r.labelVi} label={r.labelVi} value={r.value} />
            ))}
          </Stack>
        </Box>
      )}
      </Box>
    </Paper>
  )
}

function AllocLine({ label, value }: { label: string; value: number }) {
  return (
    <Box sx={allocationRowSx}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={800}>
        {fmtQty(value)}
      </Typography>
    </Box>
  )
}

export default NhonTrachSummaryMap
